import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import FlexRow from '../layout/flex/row.gjs';
import SearchInput from '../input/search/field.gjs';
import Super from '../core/base.gjs';
import styles from './search.module.scss';

const PLACEHOLDER_DEFAULT = 'Search';

const METADATA = Metadata.define({
  description:
    'Filter input rendered at the top of a menu, used to search/filter the menu items in place.',
  extends: [
    pick(ACCESSIBILITY_ARGS, ['ariaControls']),
    pick(ACTION_ARGS, ['onInput']),
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled']),
    pick(INPUT_FORM_ARGS, [{ name: 'placeholder', defaultValue: PLACEHOLDER_DEFAULT }, 'value']),
  ],
  meta: {
    onClear: Arg.action({ description: 'Called when the clear button is clicked' }),
  },
  name: 'Menu Search',
});

export default class SpdsMenuSearch extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-search';
  classesToMap = ['disabled'];
  styles = styles;

  @tracked searchInputComponent;

  get clearButtonId() {
    return this.composeSubcomponentId('clear-button');
  }

  get containerId() {
    return this.composeSubcomponentId('container');
  }

  get searchInputElement() {
    return this.searchInputComponent?.inputElement;
  }

  get placeholder() {
    return this.argOrDefault('placeholder');
  }

  get showClear() {
    return !this.args.disabled && this.args.value?.length > 0;
  }

  <template>
    <FlexRow class={{this.classes}}>
      <SearchInput
        @id={{this.containerId}}
        @registerComponent={{fn this.registerComponent "searchInputComponent"}}
        @unregisterComponent={{fn this.unregisterComponent "searchInputComponent"}}
        data-test-spds-menu-search
        {{this.setupComponent}}
        {{this.validateArguments}}
        ...attributes
        as |si|
      >
        <si.Icon />

        <si.Input
          @ariaLabel={{this.placeholder}}
          @autofocus={{@autofocus}}
          @disabled={{@disabled}}
          @id={{this.id}}
          @onInput={{@onInput}}
          @placeholder={{this.placeholder}}
          @value={{@value}}
          aria-controls={{@ariaControls}}
          data-test-spds-menu-search-input
          role="searchbox"
        />

        {{#if this.showClear}}
          <si.ClearButton
            @id={{this.clearButtonId}}
            data-test-spds-menu-search-clear
            {{on "click" @onClear}}
          />
        {{/if}}
      </SearchInput>
    </FlexRow>
  </template>
}

export const MENU_SEARCH_METADATA = SpdsMenuSearch.metadata;
